import { api, izvedi, podatki } from '../api/odjemalec';

/**
 * Push obvestila v tem brskalniku (o novih povpraševanjih): service worker {@code /sw.js}, naročnina s ključem VAPID
 * strežnika, naročnina se shrani na strežniku za prijavljenega uporabnika. iPhone/iPad: push deluje samo, ko je
 * aplikacija dodana na začetni zaslon.
 */
export type StanjePush = 'ni-podprto' | 'ios-namesti' | 'zavrnjeno' | 'vklopljeno' | 'izklopljeno';

const SW = '/sw.js';

export function podprto(): boolean {
  return (
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  );
}

function ios(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

async function obstojecaNarocnina(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration('/');
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function stanjePush(): Promise<StanjePush> {
  if (!podprto()) {
    return ios() ? 'ios-namesti' : 'ni-podprto';
  }
  if (Notification.permission === 'denied') {
    return 'zavrnjeno';
  }
  return Notification.permission === 'granted' && (await obstojecaNarocnina()) ? 'vklopljeno' : 'izklopljeno';
}

/** Ključ VAPID (base64url) → bajti za applicationServerKey. */
export function kljucVBajte(kljuc: string): Uint8Array<ArrayBuffer> {
  const b64 = kljuc.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (kljuc.length % 4)) % 4);
  const niz = atob(b64);
  const b = new Uint8Array(new ArrayBuffer(niz.length));
  for (let i = 0; i < niz.length; i++) {
    b[i] = niz.charCodeAt(i);
  }
  return b;
}

function enako(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.length) {
    return false;
  }
  const x = new Uint8Array(a);
  return x.every((v, i) => v === b[i]);
}

function naprava(): string {
  const ua = navigator.userAgent;
  const brskalnik = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\//.test(ua)
      ? 'Firefox'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Brskalnik';
  const sistem = /Android/.test(ua)
    ? 'Android'
    : ios()
      ? 'iOS'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return [brskalnik, sistem].filter(Boolean).join(' – ');
}

async function poslji(n: PushSubscription): Promise<void> {
  const j = n.toJSON();
  await izvedi(
    api.POST('/api/push/narocnina', {
      body: { endpoint: j.endpoint, p256dh: j.keys?.p256dh, auth: j.keys?.auth, naprava: naprava() },
    }),
  );
}

/** Vklopi obvestila (vpraša za dovoljenje); vrže napako s sporočilom za uporabnika. */
export async function vklopiPush(): Promise<void> {
  if (!podprto()) {
    throw new Error('Ta brskalnik ne podpira obvestil.');
  }
  const dovoljenje = await Notification.requestPermission();
  if (dovoljenje !== 'granted') {
    throw new Error('Obvestila niso dovoljena. Dovolite jih v nastavitvah brskalnika za to stran.');
  }
  const reg = await navigator.serviceWorker.register(SW, { scope: '/' });
  await navigator.serviceWorker.ready;
  const { javniKljuc } = await podatki(api.GET('/api/push/kljuc'));
  const kljuc = kljucVBajte(javniKljuc ?? '');
  let n = await reg.pushManager.getSubscription();
  if (n && !enako(n.options.applicationServerKey, kljuc)) {
    await n.unsubscribe();
    n = null;
  }
  n ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: kljuc });
  await poslji(n);
}

/** Izklopi obvestila v tem brskalniku (tudi ob odjavi, da ne prihajajo naslednjemu uporabniku). */
export async function izklopiPush(): Promise<void> {
  if (!podprto()) {
    return;
  }
  const n = await obstojecaNarocnina();
  if (!n) {
    return;
  }
  try {
    await izvedi(api.POST('/api/push/odjava', { body: { endpoint: n.endpoint } }));
  } finally {
    await n.unsubscribe();
  }
}

/** Po prijavi: obstoječo naročnino brskalnika znova zapiše za tega uporabnika (strežnik jo morda ne pozna več). */
export async function uskladiPush(): Promise<void> {
  if (!podprto() || Notification.permission !== 'granted') {
    return;
  }
  const n = await obstojecaNarocnina();
  if (n) {
    await poslji(n);
  }
}
